<template>
  <b-col sm="12" lg="12" offset="0">
    <DateModal
      modalID="spaceUtilization"
      :fetchData="fetchData"
      :dateFrom="dateFrom"
      :dateTo="dateTo"
      @date-picker-modal-emit="onDatePickerModalEmit"
    ></DateModal>
    <b-card class="backClass" style="min-height: 400px">
      <div
        slot="header"
        style="display: flex; justify-content: space-between; align-items: center"
      >
        <HeaderTitle
          :title="title"
          :dateFrom="from ? from : dateFrom"
          :dateTo="to ? to : dateTo"
          chartType="spaceUtilization"
        ></HeaderTitle>
        <HeaderControl
          :showFullscreenIcon="true"
          :showDateIcon="true"
          :showDownloadPdfIcon="true"
          chartType="spaceUtilization"
          @emit-fullscreen="toggleFullScreen"
          @emit-date="modalDateEvent('spaceUtilization')"
          @emit-last-month="setLastMonth"
          @emit-pdf-download="downloadPDF"
        ></HeaderControl>
      </div>

      <div class="chart-wrapper">
        <div v-if="loadingData" class="centered_container">
          <LoadingCard></LoadingCard>
        </div>
        <div v-else>
          <highcharts
            ref="chartRef"
            :options="chartOptions"
            class="chart"
            :constructor-type="'chart'"
          />


          <div v-if="Object.keys(formattedData).length === 0" class="text-white text-center mt-4">
            <h5>No data available for the selected dates.</h5>
          </div>
        </div>
      </div>
    </b-card>
  </b-col>
</template>

<script>
import Highcharts from "highcharts";
import DrilldownModule from "highcharts/modules/drilldown";
import { Chart } from "highcharts-vue";
import exporting from "highcharts/modules/exporting";
import HeaderControl from "../../../components/shared/HeaderControl.vue";
import HeaderTitle from "../../../components/shared/HeaderTitle.vue";
import LoadingCard from "../../../components/shared/LoadingCard.vue";
import DateModal from "../../../components/shared/DateModal.vue";
import { chartFunctionsMixin } from "../../../components/chart/addons/chartFunctionsMixin.js";

DrilldownModule(Highcharts);
exporting(Highcharts);

export default {
  name: "SpaceUtilization",
  mixins: [chartFunctionsMixin],
  components: {
    highcharts: Chart,
    DateModal,
    HeaderTitle,
    HeaderControl,
    LoadingCard,
  },
  data() {
    return {
      title: "Space Utilization",
      formattedData: {},
      drilledWarehouse: null,
      chartOptions: {
        chart: {
          type: "column",
          backgroundColor: "transparent",
          marginTop: 70,
          events: {
            drilldown: (e) => {
              if (!e.point) return;

              const chart = e.target;
              const warehouseID = e.point.drilldown;
              const warehouse = this.formattedData[warehouseID];
              if (!warehouse) return;

              this.drilledWarehouse = warehouse;
              chart.setTitle(null, { text: "", style: { color: "white" } });
              chart.legend.update({ enabled: true });

              const { dates, used, available } = warehouse;

              // Add Available series first using addSeriesAsDrilldown — this registers the drilldown level
              chart.addSeriesAsDrilldown(e.point, {
                name: "Available",
                type: "column",
                color: "#34db88",
                borderWidth: 0,
                stacking: "normal",
                showInLegend: true,
                dataLabels: {
                  enabled: true,
                  format: "{point.y:.1f}%",
                  color: "white",
                  style: { textOutline: false, fontWeight: 'bold', fontSize: '11px' }
                },
                data: dates.map((date, i) => ({ name: date, y: available[i] }))
              });

              // Add Used series using chart.addSeries — it joins the same drilldown level
              chart.addSeries({
                name: "Used",
                type: "column",
                color: "#db5151",
                borderWidth: 0,
                stacking: "normal",
                showInLegend: true,
                _isDrilldownSeries: true,
                dataLabels: {
                  enabled: true,
                  format: "{point.y:.1f}%",
                  color: "white",
                  style: { textOutline: false, fontWeight: 'bold', fontSize: '11px' }
                },
                data: dates.map((date, i) => ({ name: date, y: used[i] }))
              }, true);
            },
            drillup: (e) => {
              // Remove the manually-added "Used" series before highcharts restores the parent
              const chart = e.target;
              chart.series
                .filter(s => s.options && s.options._isDrilldownSeries)
                .forEach(s => s.remove(false));
              chart.redraw();

              this.drilledWarehouse = null;
              chart.setTitle(null, { text: "", style: { color: "white" } });
              chart.legend.update({ enabled: false });
            }
          }
        },
        title: { text: "" },
        subtitle: {
          text: "Click on a warehouse to view space utilization by date",
          style: { color: "white" }
        },
        xAxis: {
          type: "category",
          labels: { style: { color: "white" } }
        },
        yAxis: {
          min: 0,
          max: 100,
          title: { text: "", style: { color: "white" } },
          labels: { enabled: false },
          gridLineColor: "#4a5568"
        },
        legend: {
          enabled: false,
          itemStyle: { color: "white" }
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function () {
            const points = this.points || [this.point];
            const isDrilldown = points.some(p => p.series.name === 'Used' || p.series.name === 'Available');
            const header = points[0].point.name || points[0].key || this.x;
            if (isDrilldown) {
              let s = '<span style="font-size:12px; font-weight: bold; color: white">' + header + '</span><br/>';
              points.forEach(p => {
                s += '<span style="color:' + p.color + '">\u25CF</span> ' + p.series.name + ': <b>' + p.y.toFixed(2) + '%</b><br/>';
              });
              return s;
            }
            // Overview: just show warehouse name
            return '<b style="color:white">' + header + '</b>';
          }
        },
        plotOptions: {
          series: {
            borderRadius: 0,
            cursor: "pointer",
            colorByPoint: false,
            dataLabels: { enabled: false }
          },
          column: {
            stacking: 'normal'
          }
        },
        drilldown: {
          activeAxisLabelStyle: { textDecoration: "none", color: "white" },
          activeDataLabelStyle: { color: "white", textDecoration: "none" },
          allowPointDrilldown: false,
          drillUpButton: {
            relativeTo: 'spacingBox',
            position: { align: 'right', x: -10, y: 5 }
          },
          series: []
        },
        series: [{ name: "Warehouses", colorByPoint: true, data: [] }],
        credits: { enabled: false }
      }
    };
  },
  methods: {
    fetchData() {
      this.loadingData = true;
      this.drilledWarehouse = null;

      const params = {
        date_from: this.from,
        date_to: this.to,
      };

      const link = `${process.env.VUE_APP_TRACKER_BASE}/api/comcast_arc/charts/space_utilization.php`;

      this.$http.get(link, { params }).then(
        (res) => {
          this.processRawData(res.data || {});
          this.loadingData = false;
        },
        (err) => {
          console.error(err);
          this.loadingData = false;
        }
      );
    },
    processRawData(rawData) {
      const formatted = {};
      const topLevelData = [];

      Object.keys(rawData).forEach(id => {
        const warehouse = rawData[id];
        const sourceData = warehouse.data || warehouse;
        const dates = Object.keys(sourceData).filter(key => /^\d{4}-\d{2}-\d{2}$/.test(key)).sort();
        const used = dates.map(d => parseFloat(sourceData[d].used || sourceData[d].utilized || 0));
        const available = dates.map(d => parseFloat(sourceData[d].available || 0));

        formatted[id] = { name: warehouse.name, dates, used, available };

        // Show each warehouse as a uniform bar — blue
        topLevelData.push({
          name: warehouse.name,
          y: 100,
          color: "#7cb5ec",
          drilldown: id
        });
      });

      this.formattedData = formatted;
      this.chartOptions.series = [{ name: "Warehouses", colorByPoint: true, data: topLevelData }];
      // Placeholder drilldown series so Highcharts fires the drilldown event on click
      this.chartOptions.drilldown.series = Object.keys(formatted).map(id => ({
        id,
        name: formatted[id].name,
        data: []
      }));
    }
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.custom-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #4a5568;
}

.custom-table thead th {
  background-color: #1a202c;
  color: #edf2f7;
  border-bottom: 2px solid #4a5568;
}

.badge-danger {
  background-color: #db5151;
}

.badge-success {
  background-color: #34db88;
}

.centered_container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.chart {
  width: 100%;
}
</style>
